<!--
Banner that shows a message with an icon and a color based on the severity
-->
<template>
	<div :class="['rounded-lg border p-3', config.containerClasses]">
		<div class="flex items-start gap-2">
			<ion-icon :icon="severityIcon" :class="iconClasses" />
			<p :class="textClasses">
				{{ message }}
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import { IonIcon } from "@ionic/vue";
	import {
		warningOutline,
		checkmarkCircleOutline,
		alertCircleOutline,
		informationCircleOutline,
	} from "ionicons/icons";

	export type NoticeSeverity = "success" | "warning" | "danger" | "info";

	interface Props {
		severity: NoticeSeverity;
		message: string;
	}

	const props = defineProps<Props>();

	const severityConfig = {
		success: {
			icon: checkmarkCircleOutline,
			containerClasses: "bg-green-50 border-green-200",
			iconClasses: "text-green-600",
			textClasses: "text-green-800",
		},
		warning: {
			icon: warningOutline,
			containerClasses: "bg-yellow-50 border-yellow-200",
			iconClasses: "text-yellow-600",
			textClasses: "text-yellow-800",
		},
		danger: {
			icon: alertCircleOutline,
			containerClasses: "bg-red-50 border-red-200",
			iconClasses: "text-red-600",
			textClasses: "text-red-800",
		},
		info: {
			icon: informationCircleOutline,
			containerClasses: "bg-blue-50 border-blue-200",
			iconClasses: "text-blue-600",
			textClasses: "text-blue-800",
		},
	};

	const config = computed(() => severityConfig[props.severity]);

	const severityIcon = computed(() => config.value.icon);

	const iconClasses = computed(() => [
		"text-lg flex-shrink-0 mt-0.5",
		config.value.iconClasses,
	]);

	const textClasses = computed(() => ["text-sm", config.value.textClasses]);
</script>
